// docs/43 story cues: the mid-scene beats of a cinematic. The storyteller's direction pass marks
// words in the story (`genesis.cues`: a spot, the words, a sound from content/audio/story.cues.json,
// a sight, its own note); this turns them into beats on the typed text — a character offset where
// the sound plays (bank `cue.<id>`) and the sight is drawn, as the words finish appearing. A story
// with no cues at all (written before the pass, or the template) is read by the vocabulary's own
// trigger words instead, two beats a text at most. Pure, so vitest reads it (cues.test.ts).
import vocab from '../../../../../content/audio/story.cues.json';
import type { Genesis } from './genesis';

export type Sight =
  | 'FallingStar' | 'Meteors' | 'Lightning' | 'Flash' | 'Shake' | 'Rumble' | 'Fireburst' | 'Smoke' | 'Sparks'
  | 'Splash' | 'Darken' | 'Glow' | 'Sparkle' | 'Gust' | 'Shadow' | 'Eyes' | 'Birds' | 'Mist';
export type CueSpot = 'Intro' | 'Parley' | 'Cry' | 'Getaway' | 'Vow' | 'Beat';
/** One cue as the wasm decodes it (fk-schema `StoryCue`). */
export interface StoryCue { spot: CueSpot; index: number; at: string; sound: string | null; sight: Sight | null; note: string }

/** A beat on one typed text: at character `at` (the end of its words), play `sound` and draw `sight`. */
export interface Beat { at: number; sound?: string; sight?: Sight; loop?: boolean; seed: number; note?: string; /** a second sound after a delay (a star streaks, then lands far off) */ then?: { sound: string; delayMs: number } }

interface SoundDef { id: string; say: string; words: string[]; sight?: string; loop?: boolean; then?: { sound: string; delay_ms: number } }
const SOUNDS = new Map<string, SoundDef>((vocab.sounds as SoundDef[]).map((s) => [s.id, s]));
export const SIGHTS = vocab.sights.map((s) => s.id) as Sight[];
export const soundIds = (): string[] => [...SOUNDS.keys()];
export const soundSay = (id: string): string | undefined => SOUNDS.get(id)?.say;

/** The sound a cue's sound is followed by, if any. */
const thenOf = (id: string | undefined): Beat['then'] => {
  const t = id ? SOUNDS.get(id)?.then : undefined;
  return t && SOUNDS.has(t.sound) ? { sound: t.sound, delayMs: t.delay_ms } : undefined;
};

/** The bank a cue's sound plays from. */
export const cueBank = (sound: string) => `cue.${sound}`;

/** A cheap stable hash: the same words pick the same variant on every screen. */
export function cueSeed(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); }
  return h >>> 0;
}

const esc = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Where `at` lands in `text`: the end of its first appearance, matched without case and with any
 * run of spaces or punctuation between its words (the model's copy may drop a comma). -1 if absent.
 */
export function findAt(text: string, at: string): number {
  const words = at.toLowerCase().split(/[^\p{L}\p{N}'-]+/u).filter(Boolean);
  if (!words.length) return -1;
  const m = new RegExp(words.map(esc).join("[^\\p{L}\\p{N}'-]+"), 'iu').exec(text);
  return m ? m.index + m[0].length : -1;
}

/** Trigger words compiled for the fallback: a trailing `*` is a stem, a space a phrase. */
const TRIGGERS = [...SOUNDS.values()].filter((s) => !s.loop).map((s) => ({
  sound: s,
  patterns: s.words.map((w) => {
    const body = w.trim();
    const stem = body.endsWith('*');
    const parts = (stem ? body.slice(0, -1) : body).toLowerCase().split(/\s+/).filter(Boolean);
    const re = new RegExp(`(?<![\\p{L}\\p{N}])${parts.map(esc).join("[^\\p{L}\\p{N}]+")}${stem ? "[\\p{L}'-]*" : '(?![\\p{L}\\p{N}])'}`, 'giu');
    return { re, weight: parts.length + (stem ? 0 : 0.25) };
  }),
}));
/** Moods are for the storyteller to place; the words alone call them only on a phrase. */
const MOOD = /^(stinger_|heartfelt_|eerie_)/;

/** Up to `max` beats read from the words alone (stories with no direction pass). */
export function inferBeats(text: string, max = 2): Beat[] {
  const found: Array<{ at: number; score: number; sound: SoundDef }> = [];
  for (const { sound, patterns } of TRIGGERS) {
    let best: { at: number; score: number } | undefined;
    for (const p of patterns) {
      p.re.lastIndex = 0;
      let m: RegExpExecArray | null;
      // a capital mid-sentence is a name (Rookmere, Sister Wren): not an event
      while ((m = p.re.exec(text)) && /^\p{Lu}/u.test(m[0]) && m.index > 0 && !/[.!?“"]\s*$/.test(text.slice(0, m.index)));
      if (!m) continue;
      const score = p.weight * (MOOD.test(sound.id) ? 0.5 : 1);
      if (!best || score > best.score || (score === best.score && m.index < best.at)) best = { at: m.index + m[0].length, score };
    }
    if (best && best.score >= 1) found.push({ ...best, sound });
  }
  // the strongest, one per moment: two sounds on the same word are one too many
  found.sort((a, b) => b.score - a.score || a.at - b.at);
  const picked: typeof found = [];
  for (const f of found) {
    if (picked.length >= max) break;
    if (picked.some((p) => Math.abs(p.at - f.at) < 12)) continue;
    picked.push(f);
  }
  return picked.sort((a, b) => a.at - b.at).map((f) => ({ at: f.at, sound: f.sound.id, sight: f.sound.sight as Sight | undefined, seed: cueSeed(`${f.sound.id}:${text}`), then: thenOf(f.sound.id) }));
}

/**
 * The beats of one shown text: the story's own cues for `spot`/`index` found in `text` (the hero
 * names already swapped in), or — when the story carries no cues at all — the words' own.
 */
export function beatsFor(g: Genesis | undefined, spot: CueSpot, index: number, text: string): Beat[] {
  if (!g || !text) return [];
  const cues = g.cues;
  if (!cues?.length) return inferBeats(text);
  const out: Beat[] = [];
  for (const c of cues) {
    if (c.spot !== spot || c.index !== index) continue;
    const at = findAt(text, c.at);
    if (at < 0) continue;
    const sound = c.sound && SOUNDS.has(c.sound) ? c.sound : undefined;
    const sight = c.sight ?? undefined;
    if (!sound && !sight) continue;
    out.push({ at, sound, sight, loop: sound ? !!SOUNDS.get(sound)!.loop : false, seed: cueSeed(`${c.at}:${c.note}`), note: c.note, then: thenOf(sound) });
  }
  return out.sort((a, b) => a.at - b.at);
}

/**
 * docs/50: a battle beat's cues, as its banner shows its line (no typing: all at once, a beat
 * apart). The story's own cues for the beat, else the line's own trigger words.
 */
export function beatCues(g: Genesis | undefined, index: number, line: string): Beat[] {
  if (!line) return [];
  const own = g?.cues?.some((c) => c.spot === 'Beat' && c.index === index) ? beatsFor(g, 'Beat', index, line) : [];
  return own.length ? own : inferBeats(line, 1);
}
